import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { format, isAfter } from 'date-fns';
import IconButton from '@mui/material/IconButton';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import {
  AccountStatus,
  AccountTypes,
  ButtonClassNames,
  GlobalRouteNames,
  ModalVariants,
  QueryParams,
  ScreenSize,
} from '../../../../models/Common';
import { IPermission, IShares, IAddress } from '../../../../models/UserInfo';
import { useDateLocalization } from '../../../../hooks/useDateLocalization';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import { AccountIcon } from '../../../commons/icons/AccountIcon';
import { WarningSmallIcon } from '../../../commons/icons/WarningSmall';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import DateVO from '../../../../core/valueObjects/DateVO';
import { toDay } from '../../../../constants/configuration';
import {
  formatAccountType,
  getCardIconType,
  findViewValuesHelper,
  handleServicePaymentAnalytics,
} from '../../../../utils';
import { ServicesTypes, ServiceShare, LoyaltyProgram } from '../../../../models/Accounts';
import { OrderParams, UnionServicesExIds, AccountView, ExAmoView } from '../../../../models/Calculate';
import { DataLayerButtonsTypes } from '../../../../types/dataLayerTypes';
import { useWindowDimension } from '../../../../hooks/useWindowDimension';
import ServiceFooter from './ServiceFooter/ServiceFooter';
import ServiceActionSign from './ServiceActionSign/ServiceActionSign';
import { useSuspensionActivation } from '../../../../hooks/useSuspensionActivation';
import { ServicePreset, TypedService } from '../../../../models/ServicePresets';
import { useCalculate } from '../../../../hooks/useCalculate';
import { useDateFsnLocale } from '../../../../hooks/useDateFsnLocale';
import { ReactComponent as InfoIcon } from '../../../../assets/images/InfoOutlined.svg';
import { ReactComponent as ChangeService } from '../../../../assets/images/services/ChangeService.svg';
import { ReactComponent as StopService } from '../../../../assets/images/services/StopService.svg';
import { ReactComponent as RestoreService } from '../../../../assets/images/services/RestoreService.svg';
import { ReactComponent as ManageService } from '../../../../assets/images/services/ManageService.svg';
import { CustomSkeletonElement } from '../../../commons/CustomSkeletonElement';
import { CustomTooltipInfo } from '../../../commons/CustomTooltipInfo';
import ExtraServices from './ExtraServices/ExtraServices';

interface ServiceProps {
  title: string;
  isPriceDaily: boolean;
  price: number;
  expireDate: Date;
  id: number;
  statusId: number;
  isDisabled: boolean;
  loyaltyProgram?: LoyaltyProgram | null;
  login?: string;
  permissions: IPermission;
  userId: number;
  userAddresses: IAddress[];
  userTypeID: number;
  shares: IShares;
  dedicatedIP?: string;
  brizTvShareActiveTo?: string;
  brizTvShareName?: string;
  isBrizTvLogin?: boolean | null;
  serviceTypeID?: number;
  serviceAnalyticsName: string;
  shareAction: ServiceShare | null | undefined;
  isServiceActivationAvailable: boolean;
  servicePreset: ServicePreset | undefined;
  isSuspense: boolean;
  Hidden: boolean;
  transitionToBrizTVServiceId: number | undefined;
  transitionToBrizTVService?: TypedService;
  type: string;
  isUnionCtvClassic: boolean;
  isInitLoading: boolean;
  serviceExIds: number[] | UnionServicesExIds | [];
  currentServiceId: number;
}

const defaultProps = {
  login: undefined,
  loyaltyProgram: undefined,
  dedicatedIP: undefined,
  brizTvShareActiveTo: undefined,
  brizTvShareName: undefined,
  isBrizTvLogin: undefined,
  serviceTypeID: undefined,
};

export const Service: FC<ServiceProps> = ({
  statusId,
  title,
  price,
  isPriceDaily,
  expireDate,
  id,
  isDisabled,
  loyaltyProgram,
  login,
  dedicatedIP,
  brizTvShareActiveTo,
  brizTvShareName,
  shares,
  permissions,
  userId,
  userAddresses,
  userTypeID,
  isBrizTvLogin,
  serviceTypeID,
  serviceAnalyticsName,
  shareAction,
  isServiceActivationAvailable,
  servicePreset,
  isSuspense,
  Hidden,
  transitionToBrizTVServiceId,
  transitionToBrizTVService,
  type,
  isUnionCtvClassic,
  isInitLoading,
  serviceExIds,
  currentServiceId,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { formatDate } = useDateLocalization();
  const dispatch = useAppDispatch();
  const { openModal } = modalSlice.actions;
  const isShareStillActive = brizTvShareActiveTo && isAfter(DateVO.create(brizTvShareActiveTo.toString()).get(), toDay);
  const { width } = useWindowDimension();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const { isSuspensionAvailable, isCurrentServiceSuspension, handleSuspensionActivate } =
    useSuspensionActivation(servicePreset);
  const accountType = formatAccountType(type);
  const [paramsForCalculation] = useState<OrderParams>({
    accountId: id.toString(),
    accountType,
    ServiceExIDs: serviceExIds,
  });
  const canRestoreService = !!servicePreset?.PreviousPayedService;
  const iconType = canRestoreService
    ? getCardIconType(accountType, servicePreset?.PreviousPayedService?.Type?.id)
    : getCardIconType(type, serviceTypeID);
  const calculable = (price > 0 || (price === 0 && canRestoreService)) && !isPriceDaily && !isDisabled;
  const availableConnectedExServices = !!servicePreset?.ServicesEx?.filter((s) => s.IsConnected).length;
  const { isLoading, currentCalculation } = useCalculate(paramsForCalculation, !calculable);
  const isDedicatedIpAvailable = accountType !== 'ctv' && accountType !== 'iptv' && !isPriceDaily;
  const canPauseService =
    !canRestoreService &&
    (!!servicePreset?.SuspendService ||
      (permissions?.Info?.ShowBonuses &&
        isSuspensionAvailable &&
        accountType !== 'iptv' &&
        isServiceActivationAvailable &&
        !isPriceDaily));
  const showCtvControls =
    accountType === 'ctv' && (!!servicePreset?.SuspendService || !!servicePreset?.PreviousPayedService);
  const canChangeService =
    (userTypeID !== 2 || (isServiceActivationAvailable && userTypeID === 2)) &&
    accountType !== 'ctv' &&
    accountType !== 'iptv';
  /* parsedDate - Необходимо для правильного парсинга даты на платформах с iOs 15 */
  const parsedDate = DateVO.create(expireDate.toString()).get(true);
  const nextPaymentDateFormat = selectedLanguage === 'en' ? 'MMMM d, yyyy' : 'd MMMM yyyy';
  const nextPaymentDate = format(parsedDate, nextPaymentDateFormat, {
    locale: fnsLocale,
  });
  // const activatedTurboGig = shares?.Activated.find((item) => item.id === turboGigShareId);
  const isAddBrizTvButtonAvailable =
    !isDisabled && serviceTypeID === ServicesTypes.INTERNET && !isSuspense && accountType === 'union';

  const discountPercent =
    currentCalculation.discountAmount > 0 && isCurrentServiceSuspension && !currentCalculation.discountPercent
      ? findViewValuesHelper(currentCalculation.view, 'ex_amo')[0].dsc_percent
      : currentCalculation.discountPercent;

  const handleActivate = (isNotShare: boolean, currentService?: string, buttonName?: string) => {
    history.push({
      pathname: isPriceDaily ? GlobalRouteNames.DailyServicePayment : GlobalRouteNames.ServicePayment,
      search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${accountType}&${QueryParams.ServiceId}=${currentServiceId}`,
    });
    if (isNotShare && currentService && buttonName) {
      handleServicePaymentAnalytics(currentService, buttonName);
    }
  };

  const handleManagement = () => {
    history.push({
      pathname: GlobalRouteNames.ServiceManagement,
      search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${accountType}`,
    });
  };

  const handleCallback = () => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.RestoreCallback,
        content: {
          service: canRestoreService ? servicePreset?.PreviousPayedService?.Name : title,
          accountType,
        },
      }),
    );
  };

  const handleChangeAccount = (serviceId: number, serviceType: AccountTypes | '') => {
    const handleProceed = () => {
      history.push({
        pathname: GlobalRouteNames.ChangeService,
        search: `?${QueryParams.AccountId}=${serviceId}&${QueryParams.AccountType}=${serviceType}`,
      });
    };

    if (Hidden || shareAction) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.Confirmation,
          content: {
            notice: t(`serviceManagement.${Hidden ? 'limitedForConnection' : 'actionChangeNotice'}`),
            handleProceed,
          },
        }),
      );
      return;
    }
    handleProceed();
  };

  const handleOpenCallbackModal = (serviceRequest: string, serviceName: string, serviceTitle: string) => {
    const site = `Сайт: ${process.env.REACT_APP_NAME || ''}.`;
    const agreement = `№ договора: ${userId}.`;
    const address = `Домашний адрес: ул. ${userAddresses[0]?.Street?.NameRu} дом ${userAddresses[0]?.House}.`;
    dispatch(
      openModal({
        modalVariants: ModalVariants.Callback,
        content: {
          service: serviceName,
          title: serviceTitle,
          comment: `${site} ${serviceRequest} ${agreement} ${address}`,
        },
      }),
    );
  };

  const handleAddBrizTV = () => {
    const serviceIdAfterTransitionToBrizTV = transitionToBrizTVServiceId && transitionToBrizTVServiceId !== 0;

    const handleProceed = () => {
      history.push({
        pathname: GlobalRouteNames.ServicePayment,
        search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${accountType}&${QueryParams.ServiceId}=${
          transitionToBrizTVServiceId || currentServiceId
        }&${QueryParams.IsChangeService}=${!!serviceIdAfterTransitionToBrizTV}`,
      });
    };

    if (!permissions?.Services?.Change) {
      handleOpenCallbackModal(
        'Запрос на подключение услуги: BRIZ.TV.',
        'BRIZ.TV',
        t('changeService.serviceActivationRequest'),
      );
      return;
    }

    if (shareAction && serviceIdAfterTransitionToBrizTV) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.Confirmation,
          content: {
            notice: t('serviceManagement.actionChangeNotice'),
            handleProceed,
          },
        }),
      );
      return;
    }
    handleProceed();
  };

  const handleConnectFilms = () => {
    const serviceIdAfterTransitionToBrizTV = transitionToBrizTVServiceId && transitionToBrizTVServiceId !== 0;

    const handleProceed = () => {
      history.push({
        pathname: GlobalRouteNames.ServicePayment,
        search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${accountType}&${
          QueryParams.ConnectFilms
        }=true&${QueryParams.ServiceId}=${transitionToBrizTVServiceId || currentServiceId}&${
          QueryParams.IsChangeService
        }=${!!serviceIdAfterTransitionToBrizTV}`,
      });
    };

    if (!permissions?.ServicesEx?.Activate) {
      handleOpenCallbackModal(
        `Запрос на подключение услуги: ${t('servicePayment.films')}.`,
        t('servicePayment.films'),
        t('changeService.serviceActivationRequest'),
      );
      return;
    }

    if (shareAction && serviceIdAfterTransitionToBrizTV) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.Confirmation,
          content: {
            notice: t('serviceManagement.actionChangeNotice'),
            handleProceed,
          },
        }),
      );
      return;
    }
    handleProceed();
  };

  const handleConnectStaticIP = () => {
    if (!permissions?.ServicesEx?.Activate) {
      handleOpenCallbackModal(
        `Запрос на подключение услуги: ${t('servicePayment.staticIp')}.`,
        t('servicePayment.staticIp'),
        t('changeService.serviceActivationRequest'),
      );
      return;
    }
    history.push({
      pathname: GlobalRouteNames.ServicePayment,
      search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${accountType}&${QueryParams.ConnectStaticIp}=true&${QueryParams.ServiceId}=${currentServiceId}`,
    });
  };

  const handleSuspense = () => {
    const handleProceed = () => {
      handleSuspensionActivate(id, accountType, title);
    };

    if (!permissions?.Services?.Suspend) {
      handleOpenCallbackModal(`Запрос на приостановку: ${title}.`, title, t('changeService.serviceSuspendRequest'));
      return;
    }

    if (shareAction && !shareAction.CanSuspend) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.Confirmation,
          content: {
            notice: t('serviceManagement.actionChangeNotice'),
            handleProceed,
          },
        }),
      );
      return;
    }
    dispatch(
      openModal({
        modalVariants: ModalVariants.ServiceSuspension,
        content: {
          title: t('common.attention'),
          handleProceed,
          service: title,
          ...(isUnionCtvClassic || (accountType === 'ctv' && servicePreset?.Access?.TypeID !== 9)
            ? { notice: t('modals.ctvRestore', { price: 100 }) }
            : {}),
        },
      }),
    );
  };

  const handleRestore = () => {
    history.push({
      pathname: GlobalRouteNames.ServicePayment,
      search: `?${QueryParams.AccountId}=${id}&${QueryParams.AccountType}=${accountType}&${QueryParams.ServiceId}=${servicePreset?.PreviousPayedService?.id}&isChangeService=true`,
    });
  };

  const getFooterCard = (
    screenWidth: number,
    serviceType: AccountTypes | '',
    showCtv: boolean,
    isDisabledAccount: boolean,
  ) => {
    return screenWidth >= ScreenSize.Tablet && (serviceType !== 'ctv' || showCtv) && !isDisabledAccount ? (
      <div className="service-card__footer">
        {isInitLoading ? (
          <CustomSkeletonElement
            skeletonContainerClassName="service-card-skeleton__manage"
            skeletonChildrenClassName="service-card-skeleton__manage__btn skeleton"
            childrenCount={3}
          />
        ) : (
          <ServiceFooter
            id={id}
            accountType={accountType}
            handleChange={handleChangeAccount}
            handleSuspense={handleSuspense}
            handleRestore={handleRestore}
            handleManage={handleManagement}
            isPauseAvailable={canPauseService}
            isRestoreAvailable={canRestoreService && (price > 0 || availableConnectedExServices)}
            isServiceChangingAvailable={canChangeService}
          />
        )}
      </div>
    ) : null;
  };

  const getTooltipService = (serviceName: string, servicePrice: string) => {
    return (
      <div className="custom-tooltip__service">
        {serviceName} <strong style={{ fontWeight: 700, textAlign: 'right' }}>{servicePrice}</strong>
      </div>
    );
  };

  const getTooltipPrice = () => {
    if (isPriceDaily || isDisabled) {
      return (
        <div className="custom-tooltip__data">
          {getTooltipService(title, `${price} ${isPriceDaily ? t('services.uahPerDay') : t('services.uahPerMonth')}`)}
        </div>
      );
    }
    const keyView = Object.keys(currentCalculation.view)[0];
    const calculateView = currentCalculation.view[keyView]?.[0] as AccountView;
    if (!calculateView) return null;
    return (
      <div className="custom-tooltip__data">
        <div className="custom-tooltip__service">
          {calculateView.name}
          <strong style={{ fontWeight: 700, textAlign: 'right' }}>{`${calculateView.service_amo} ${t(
            'services.uahPerMonth',
          )}`}</strong>
        </div>
        {[...calculateView.ex_amo]
          .sort((a, b) => b.service_amo - a.service_amo)
          .map((i: ExAmoView) => (
            <div key={i.id} className="custom-tooltip__service">
              {i.name}
              <strong style={{ fontWeight: 700, textAlign: 'right' }}>{`${i.service_amo} ${t(
                'services.uahPerMonth',
              )}`}</strong>
            </div>
          ))}

        {currentCalculation.discountAmount > 0 &&
          getTooltipService(
            `${t('common.discount')} ${discountPercent ? `${discountPercent}%` : ''}`,
            `${-1 * currentCalculation.discountAmount} ${t('services.uahPerMonth')}`,
          )}
      </div>
    );
  };

  const showButtonRestore = () => {
    return (
      <div className={`service-card__info-container__button ${availableConnectedExServices ? 'prolongIP' : ''}`}>
        <div className="service-card__info-container__button-price">
          <span>{t('servicePayment.totalSum')}</span>
          {isLoading ? (
            <CustomSkeletonElement
              skeletonContainerClassName="service-card-skeleton__checkout"
              skeletonChildrenClassName="service-card-skeleton__checkout__btn skeleton"
              childrenCount={1}
            />
          ) : (
            <>
              {currentCalculation.totalAmount > 0 && (
                <CustomTooltipInfo tooltipText="" element={<>{getTooltipPrice()}</>} placement="top">
                  <InfoIcon />
                </CustomTooltipInfo>
              )}
              <strong>{currentCalculation.totalAmount}</strong>{' '}
              {isPriceDaily ? ` ${t('services.uahPerDay')}` : ` ${t('services.uahPerMonth')}`}
            </>
          )}
        </div>
        {isDisabled ? (
          <CustomMUIButton
            label={t('buttons.restore')}
            handleClick={handleCallback}
            className={ButtonClassNames.SecondaryOutlined}
          />
        ) : (
          <>
            {availableConnectedExServices ? (
              <CustomMUIButton
                label={t('buttons.prolongIp')}
                handleClick={() => handleActivate(true, serviceAnalyticsName, DataLayerButtonsTypes.ACTIVATE)}
                className={ButtonClassNames.PrimaryFulfilled}
                isDisabled={!isServiceActivationAvailable}
                isBold
              />
            ) : (
              <CustomMUIButton
                label={t('buttons.restore')}
                handleClick={handleRestore}
                className={ButtonClassNames.PrimaryFulfilled}
                isBold
              />
            )}
          </>
        )}
      </div>
    );
  };

  const showButtonPay = () => {
    return (
      <div className={`service-card__info-container__button ${price > 0 ? '' : 'clean'}`}>
        <div className="service-card__info-container__button-price">
          {price > 0 && (
            <>
              <span>{t('servicePayment.totalSum')}</span>
              {isLoading ? (
                <CustomSkeletonElement
                  skeletonContainerClassName="service-card-skeleton__checkout"
                  skeletonChildrenClassName="service-card-skeleton__checkout__btn skeleton"
                  childrenCount={1}
                />
              ) : (
                <>
                  <CustomTooltipInfo tooltipText="" element={<>{getTooltipPrice()}</>} placement="top">
                    <InfoIcon />
                  </CustomTooltipInfo>
                  <strong>{isPriceDaily || isDisabled ? price : currentCalculation.totalAmount}</strong>{' '}
                  {isPriceDaily ? ` ${t('services.uahPerDay')}` : ` ${t('services.uahPerMonth')}`}
                </>
              )}
            </>
          )}
        </div>
        <div className="service-card__info-container__buttons">
          {isDisabled ? (
            <CustomMUIButton
              label={t('buttons.restore')}
              handleClick={handleCallback}
              className={ButtonClassNames.SecondaryOutlined}
            />
          ) : (
            <>
              {isAddBrizTvButtonAvailable && transitionToBrizTVService ? (
                <CustomMUIButton
                  label={
                    transitionToBrizTVService.Price - price > 0
                      ? t('buttons.addBrizTV', { price: transitionToBrizTVService.Price - price })
                      : t('buttons.addBrizTVNull')
                  }
                  handleClick={handleAddBrizTV}
                  className={ButtonClassNames.SecondaryOutlined}
                  isBold
                  noWrap
                />
              ) : null}
              <CustomMUIButton
                label={t('buttons.proceedToCheckout')}
                handleClick={() => handleActivate(true, serviceAnalyticsName, DataLayerButtonsTypes.ACTIVATE)}
                className={ButtonClassNames.PrimaryFulfilled}
                isDisabled={!isServiceActivationAvailable}
                isBold
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const getLoyaltyInfo = (info: LoyaltyProgram) => {
    return (
      <a
        className="loyalty"
        target="_blank"
        rel="noreferrer"
        href={`https://www.briz.ua/${i18n.language === 'uk' ? '' : `${i18n.language}/`}7-dney`}
      >
        {t('serviceManagement.loyaltyProgram', {
          name: info.Name,
          toDate: formatDate(info.ToDate, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            locale: 'uk',
          }),
        })}
      </a>
    );
  };

  const activatedShare = useMemo(
    () => shares.Activated.find((i) => i.id === shareAction?.id),
    [shareAction?.id, shares.Activated],
  );

  return (
    <div className={`service-card card card__extra ${shareAction ? 'card__action' : ''}`}>
      {shareAction ? (
        <ServiceActionSign
          title={`${shareAction.StopDate ? t('serviceManagement.offerValidThrough') : ''} 
          ${
            shareAction.StopDate
              ? formatDate(activatedShare ? activatedShare.ActiveTo : shareAction.StopDate, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  locale: 'uk',
                })
              : ''
          }`}
        />
      ) : null}
      {(accountType !== 'ctv' || showCtvControls) && !isDisabled ? (
        <div className="service-card__divider">
          {canChangeService ||
          canPauseService ||
          (canRestoreService && (price > 0 || availableConnectedExServices)) ||
          accountType !== 'ctv' ? (
            <hr />
          ) : null}
        </div>
      ) : null}
      <div className="service-card__info-container">
        <div className="service-card__info-container__tariff">
          <div className="service-card__info-container__tariff__info">
            <div className="service-card__info-container__tariff__info__header">
              <div className="service-card__info-container__tariff__info__header__icon">
                <AccountIcon accountType={iconType} />
                {canRestoreService ? <WarningSmallIcon className="icon-warning" /> : null}
              </div>
              {/* eslint-disable-next-line no-nested-ternary */}
              {width < ScreenSize.Tablet && (accountType !== 'ctv' || showCtvControls) && !isDisabled ? (
                isInitLoading ? (
                  <CustomSkeletonElement
                    skeletonContainerClassName="service-card-skeleton__container__info__header__buttons"
                    skeletonChildrenClassName="service-card-skeleton__container__info__header__buttons__btn skeleton"
                    childrenCount={3}
                  />
                ) : (
                  <div className="service-card__info-container__tariff__info__header__control">
                    {canChangeService ? (
                      <IconButton onClick={() => handleChangeAccount(id, accountType)}>
                        <ChangeService />
                      </IconButton>
                    ) : null}
                    {canPauseService ? (
                      <IconButton onClick={handleSuspense}>
                        <StopService />
                      </IconButton>
                    ) : null}
                    {canRestoreService && (price > 0 || availableConnectedExServices) ? (
                      <IconButton className="icon-accent" onClick={handleRestore}>
                        <RestoreService />
                      </IconButton>
                    ) : null}
                    {accountType !== 'ctv' ? (
                      <IconButton onClick={handleManagement}>
                        <ManageService />
                      </IconButton>
                    ) : null}
                  </div>
                )
              ) : null}
            </div>
            <div className="service-card__info-container__tariff__info-service">
              <div className="service-card__info-container__tariff__info-service__name">
                <div className="service-card__info-container__tariff__info-service__name__header">
                  <p>{canRestoreService ? servicePreset?.PreviousPayedService?.Name : title}</p>
                  {login && <p className="login">{login}</p>}
                </div>
              </div>
              <p
                className={`service-card__info-container__tariff__info-service__expire
                  ${statusId === AccountStatus.Active ? `${canRestoreService ? 'suspended' : 'active'}` : ''}
                  ${statusId === AccountStatus.WaitingForPayment ? `${loyaltyProgram ? '' : 'waiting'}` : ''}
                `}
              >
                {statusId === AccountStatus.Active &&
                  t(`serviceManagement.${canRestoreService ? 'suspendedUntil' : 'nextPaymentDate'}`)}{' '}
                {statusId === AccountStatus.Active && nextPaymentDate}
                {statusId === AccountStatus.WaitingForPayment &&
                  (loyaltyProgram ? getLoyaltyInfo(loyaltyProgram) : t('serviceManagement.serviceNonActive'))}
                {statusId !== AccountStatus.Active &&
                  statusId !== AccountStatus.WaitingForPayment &&
                  (loyaltyProgram ? getLoyaltyInfo(loyaltyProgram) : t('serviceManagement.serviceNonActive'))}
              </p>
            </div>
            {width >= ScreenSize.Tablet ? (
              <>{canRestoreService && price === 0 ? showButtonRestore() : showButtonPay()}</>
            ) : null}
          </div>

          {((brizTvShareName || isBrizTvLogin || isDedicatedIpAvailable) && !canRestoreService) ||
          (canRestoreService && (price > 0 || availableConnectedExServices)) ? (
            <div className="service-card__info-container__tariff__extra">
              <div className="service-card__info-container__tariff__extra-service">
                {isBrizTvLogin && isShareStillActive ? (
                  <div className="service-card__info-container__tariff__extra-service__additional">
                    <p>
                      {brizTvShareName}:{' '}
                      <span className="active">
                        {t('services.activeUntil', {
                          val: formatDate(brizTvShareActiveTo || '')?.replace(/\s?р\.$/, ''),
                        })}
                      </span>
                    </p>
                  </div>
                ) : null}
                <ExtraServices
                  service={servicePreset}
                  dedicatedIp={dedicatedIP || ''}
                  isDisabled={isDisabled}
                  handleConnectStaticIP={handleConnectStaticIP}
                  isBrizTvLogin={!!isBrizTvLogin}
                  isShareStillActive={!!isShareStillActive}
                  isCurrentServiceSuspension={!!isCurrentServiceSuspension}
                  handleConnectFilms={handleConnectFilms}
                />
              </div>
            </div>
          ) : null}
        </div>
        {width < ScreenSize.Tablet ? (
          <>{canRestoreService && price === 0 ? showButtonRestore() : showButtonPay()}</>
        ) : null}
      </div>
      {getFooterCard(width, accountType, showCtvControls, isDisabled)}
    </div>
  );
};

Service.defaultProps = defaultProps;
